import { useState } from 'react';
import { IconZoomOutline } from '@iwoca/icons';
import { Input } from '@iwoca/orion';
import {
  ColumnFiltersState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { AddNewUserButton } from './AddNewUserButton';
import { ActionsCell } from './cells/ActionsCell';
import { NameCell } from './cells/NameCell';
import { PermissionsCell } from './cells/PermissionsCell';
import { StatusCell } from './cells/StatusCell';
import { TableHeader } from './TableHeader/TableHeader';
import {
  COLUMN_IDS,
  filterByPermissions,
  filterByRole,
  filterByStatus,
  getFullName,
} from './UsersTable.helpers';
import styles from './UsersTable.module.css';
import { TIntroducerListItem } from './UsersTable.types';
import { UsersTableEmptyState } from './UsersTableEmptyState/UsersTableEmptyState';
import { UsersTableSkeleton } from './UsersTableSkeleton/UsersTableSkeleton';

const columnHelper = createColumnHelper<TIntroducerListItem>();

const columns = [
  columnHelper.accessor(getFullName, {
    id: COLUMN_IDS.name,
    header: 'Name',
    size: 200,
    cell: ({ row }) => <NameCell user={row.original} />,
    enableSorting: true,
    enableColumnFilter: true,
    filterFn: filterByRole,
    meta: { filterVariant: 'select' },
  }),
  columnHelper.accessor('is_active', {
    id: COLUMN_IDS.status,
    header: 'Status',
    size: 150,
    cell: (info) => <StatusCell isActive={info.getValue()} />,
    enableSorting: true,
    enableColumnFilter: true,
    filterFn: filterByStatus,
    meta: { filterVariant: 'select' },
  }),
  columnHelper.accessor('permissions', {
    id: COLUMN_IDS.permissions,
    header: 'Account permissions',
    size: 350,
    cell: (info) => <PermissionsCell permissions={info.getValue()} />,
    enableSorting: false,
    enableColumnFilter: true,
    filterFn: filterByPermissions,
    meta: { filterVariant: 'select' },
  }),
  columnHelper.accessor('email', {
    id: COLUMN_IDS.email,
    header: 'Email',
    size: 300,
    cell: (info) => info.getValue() ?? '',
    enableSorting: false,
    enableColumnFilter: false,
  }),
  columnHelper.accessor('phone_number', {
    id: COLUMN_IDS.phone,
    header: 'Phone',
    size: 150,
    cell: (info) => info.getValue(),
    enableSorting: false,
    enableColumnFilter: false,
  }),
  columnHelper.display({
    id: COLUMN_IDS.actions,
    header: 'Actions',
    size: 100,
    cell: ({ row }) => <ActionsCell user={row.original} />,
  }),
];

type TProps = {
  users: TIntroducerListItem[];
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  isLoading: boolean;
  currentIntroducerId: string | null;
};

export const UsersTable = ({
  users,
  searchQuery,
  onSearchQueryChange,
  isLoading,
  currentIntroducerId,
}: TProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const isSortOrFilterActive = sorting.length > 0 || columnFilters.length > 0;
  const pinnedTopIds =
    !isSortOrFilterActive &&
    currentIntroducerId &&
    users.some((user) => user.id === currentIntroducerId)
      ? [currentIntroducerId]
      : [];

  const table = useReactTable({
    data: users,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (user) => user.id,
    keepPinnedRows: true,
    state: { sorting, columnFilters, rowPinning: { top: pinnedTopIds } },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
  });

  const rows = [...table.getTopRows(), ...table.getCenterRows()];

  const getEmptyStateMessage = () => {
    if (columnFilters.length > 0) return 'No users match your filters';
    return 'No users yet';
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <Input
          type="search"
          aria-label="Search by name or email"
          placeholder="Search by name or email"
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          wrapperClassName={styles.search}
          iconRight={
            <IconZoomOutline aria-hidden className={styles.searchIcon} />
          }
        />
        <AddNewUserButton />
      </div>
      <div className={styles.tableContainer}>
        <table
          className={clsx(styles.table, {
            [styles.tableEmpty]: !isLoading && rows.length === 0,
          })}
        >
          <colgroup>
            {table.getHeaderGroups()[0].headers.map((header) => (
              <col key={header.id} style={{ width: header.getSize() }} />
            ))}
          </colgroup>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className={styles.header}>
                    <TableHeader header={header} />
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              <UsersTableSkeleton
                columnCount={columns.length}
                cellClassName={styles.cell}
              />
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className={styles.emptyState}>
                  {searchQuery ? (
                    <UsersTableEmptyState />
                  ) : (
                    getEmptyStateMessage()
                  )}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className={styles.row}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className={styles.cell}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
